<template>
  <div v-if="open" class="completion-menu-backdrop" @click="open = false"></div>
  <ActionMenu
    v-model="open"
    class="completion-menu"
    position="center"
    menu-id="completion-actions"
  >
    <template #button>
      <button
        class="completion-menu__toggle"
        type="button"
        aria-label="Guide actions"
        aria-controls="completion-actions"
        :aria-expanded="open"
      >
        <span class="iconfont imore"></span>
      </button>
    </template>

    <Action @click.prevent="openHighlights">
      <span
        class="completion-menu__icon completion-menu__icon--highlights"
      ></span>
      <span class="completion-menu__item-content">
        <span>Highlights</span>
        <span
          v-if="data.highlights.count"
          class="completion-menu__badge completion-menu__badge--highlight"
        >
          <span class="completion-menu__badge-text">
            {{ data.highlights.count }}
          </span>
        </span>
        <span v-else class="completion-menu__add-highlight">
          Add highlight
        </span>
      </span>
    </Action>

    <Action v-if="data.exercises.length" @click.prevent="openExercises">
      <span
        class="completion-menu__icon completion-menu__icon--exercises"
      ></span>
      <span class="completion-menu__item-content">
        <span>Exercises</span>
        <span
          class="completion-menu__badge"
          :class="{
            'completion-menu__badge--complete': allExercisesCompleted,
          }"
        >
          <span class="completion-menu__badge-text">
            {{ completedExerciseCount }}/{{ data.exercises.length }}
          </span>
        </span>
      </span>
    </Action>

    <Action @click.prevent="save">
      <span
        class="completion-menu__icon completion-menu__icon--save"
        :class="{ 'completion-menu__icon--saved': saved }"
      ></span>
      Save
    </Action>

    <Action
      v-if="canDownload"
      :href="downloadUrl"
      :download="downloadName"
      @click="download"
    >
      <span
        class="completion-menu__icon completion-menu__icon--download"
      ></span>
      Download
    </Action>
    <Action v-else @click.prevent="openExport">
      <span
        class="completion-menu__icon completion-menu__icon--download"
      ></span>
      Download
    </Action>

    <Action
      v-if="data.book.amazon_link && !isAndroid"
      :href="data.book.amazon_link"
      @click="buyOnAmazon"
    >
      <span class="completion-menu__icon completion-menu__icon--buy"></span>
      Buy on Amazon
    </Action>

    <Action @click.prevent="openDiscussion">
      <span class="completion-menu__icon completion-menu__icon--discuss"></span>
      Discuss
    </Action>

    <Action @click.prevent="resetProgress">
      <span class="completion-menu__icon completion-menu__icon--reset"></span>
      Mark as not started
    </Action>
  </ActionMenu>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'
import { useRouter } from 'vue-router'

import { Mixpanel } from '@/analytics/mixpanel'
import type { CompletionFlowAction } from '@/analytics/mixpanel/mixpanel'
import type { CompletionPopupData } from '@/models/completion'
import { DocExportView } from '@/models/doc.export'
import { assertNotNull } from '@/helpers/typing'
import {
  DOCS_USER_DATA_CACHE,
  DOC_KEYS,
  HOME_CACHE,
  resetCacheByKeys,
  updateDocsUserDataCache,
} from '@/helpers/cache'
import { auth } from '@/services/auth'
import { backend } from '@/services/backend'
import { playerSession } from '@/services/player/session'
import { platform } from '@/services/platform'
import { apiURL } from '@/services/request'
import { appNotifications } from '@/services/notification.app'

import ActionMenu from '@/app/components/ActionMenu.vue'
import Action from '@/app/components/ActionMenuItem.vue'

const props = defineProps<{
  data: CompletionPopupData
}>()

const emit = defineEmits<{
  (event: 'dismiss'): void
}>()

const open = defineModel<boolean>({ default: false })
const router = useRouter()
const isAndroid = ref(false)
const saved = ref(props.data.is_favorite)

const completedExerciseCount = computed(
  () => props.data.exercises.filter((exercise) => exercise.completed).length,
)
const allExercisesCompleted = computed(
  () => completedExerciseCount.value === props.data.exercises.length,
)
const exportView = computed(
  () => new DocExportView(props.data.book, assertNotNull(auth.getUser())),
)
const canDownload = computed(() => exportView.value.canDownload())
const downloadUrl = computed(
  () => `${apiURL}pdfdocs?token=${props.data.book.doc_export_stats.token}`,
)
const downloadName = computed(
  () => `Shortform Summary - ${props.data.book.title.replace('/', '-')}.pdf`,
)

onBeforeMount(async () => {
  isAndroid.value = await platform.isNativeAndroidApp()
})

function close(): void {
  open.value = false
}

function dismiss(): void {
  close()
  emit('dismiss')
}

function dismissAndOpen(route: Parameters<typeof router.push>[0]): void {
  dismiss()
  void router.push(route)
}

function trackAction(action: CompletionFlowAction): void {
  Mixpanel.trackCompletionFlowAction(props.data.book.id, action)
}

function openHighlights(): void {
  trackAction('open-highlights')
  dismissAndOpen({
    name: 'book.highlights',
    params: { url_slug: props.data.book.url_slug },
  })
}

function openExercises(): void {
  trackAction('open-exercises')
  dismissAndOpen({
    name: 'book.page',
    params: {
      url_slug: props.data.book.url_slug,
      page_url_slug: props.data.exercises[0].url_slug,
    },
  })
}

async function save(): Promise<void> {
  trackAction('save')
  close()
  if (saved.value) {
    return
  }

  await backend.favorite({
    id: props.data.book.id,
    title: props.data.book.title,
    doc_type: 'book',
    is_free: props.data.book.is_free,
    summary_words: props.data.book.summary_words,
  })
  saved.value = true
  appNotifications.success({ title: 'Saved to Favorites' })
  await resetCacheByKeys([HOME_CACHE, DOCS_USER_DATA_CACHE])
}

function download(event: MouseEvent): void {
  trackAction('download')
  close()
  if (isAndroid.value) {
    event.preventDefault()
    void platform.openUrl(downloadUrl.value)
  }
}

function openExport(): void {
  trackAction('download')
  dismissAndOpen({
    name: 'book.export',
    params: { url_slug: props.data.book.url_slug },
  })
}

function openDiscussion(): void {
  trackAction('discuss')
  dismissAndOpen({
    name: 'book.community',
    params: { url_slug: props.data.book.url_slug },
  })
}

async function resetProgress(): Promise<void> {
  trackAction('mark-as-not-started')
  close()
  // The popup can appear over the guide that just finished playing. Stop its
  // audio session so replaying it will use the reset chapter progress.
  if (playerSession.value.doc?.id === props.data.book.id) {
    await playerSession.value.stop()
  }
  const userData = await backend.resetBookProgress(props.data.book.id)
  await Promise.all([
    updateDocsUserDataCache(props.data.book.id, userData),
    resetCacheByKeys(DOC_KEYS.book),
  ])
  dismissAndOpen({
    name: 'book.page',
    // Navigation within this guide normally reuses the reader. Ask it to load
    // fresh data so it no longer shows the progress we have just reset.
    query: { reload: '1' },
    params: {
      url_slug: props.data.book.url_slug,
      page_url_slug: '',
    },
  })
}

function buyOnAmazon(): void {
  trackAction('buy-on-amazon')
  close()
}
</script>

<style lang="scss" scoped>
.completion-menu {
  --horizontal-distance: 0;

  &__toggle {
    display: grid;
    width: 48px;
    height: 48px;
    place-items: center;
    border: 1px solid $c-black-3;
    border-radius: 50%;
    color: $c-black-3;
    font-size: 24px;
  }

  &__item-content {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__icon {
    display: block;
    flex: 0 0 24px;
    width: 24px;
    height: 24px;
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;

    &--highlights {
      background-image: url('@/assets.app/images/completion-menu/highlights.svg');
    }

    &--exercises {
      background-image: url('@/assets.app/images/completion-menu/exercises.svg');
    }

    &--save {
      background-image: url('@/assets.app/images/completion-menu/save.svg');
    }

    &--saved {
      background-image: url('@/assets.app/images/completion-menu/save-filled.svg');
    }

    &--download {
      background-image: url('@/assets.app/images/completion-menu/download.svg');
    }

    &--buy {
      background-image: url('@/assets.app/images/completion-menu/buy.svg');
    }

    &--discuss {
      background-image: url('@/assets.app/images/completion-menu/discuss.svg');
    }

    &--reset {
      background-image: url('@/assets.app/images/completion-menu/reset.svg');
    }
  }

  &__badge {
    display: inline-flex;
    height: 24px;
    align-items: center;
    justify-content: center;
    padding: 0 12px;
    border-radius: 100px;
    background: $c-border-7;
    font-size: 12px;
    line-height: 16px;
    white-space: nowrap;

    &--highlight {
      min-width: 32px;
      background: $c-yellow;
    }

    &--complete {
      background: $c-green-2;
      color: $c-white;
    }
  }

  &__badge-text {
    transform: translateY(1px);
  }

  &__add-highlight {
    display: inline-flex;
    height: 32px;
    align-items: center;
    padding: 0 12px;
    border: 1px solid $c-yellow;
    border-radius: 53px;
    background: $c-yellow;
    color: $c-black-15;
    font-size: 12px;
    line-height: 16px;
    white-space: nowrap;
  }

  :deep(.action-menu__list) {
    width: min(320px, calc(100vw - 32px));
  }

  :deep(.action-menu__item) {
    min-height: 48px;
    color: $c-black-15;
  }

  @media (max-width: $w-mobile-s) {
    :deep(.action-menu__list) {
      position: fixed;
      top: auto;
      right: 0;
      bottom: 0;
      left: 0;
      width: 100%;
      max-height: calc(100vh - env(safe-area-inset-top));
      overflow-y: auto;
      padding: 40px 24px calc(28px + env(safe-area-inset-bottom));
      border-radius: 20px 20px 0 0;
      transform: none;

      &::before {
        display: block;
        position: absolute;
        top: 16px;
        right: auto;
        bottom: auto;
        left: 50%;
        width: 80px;
        height: 4px;
        border: 0;
        border-radius: 100px;
        background: $c-black-13;
        clip-path: none;
        transform: translateX(-50%);
      }
    }

    :deep(.action-menu__item) {
      min-height: 56px;
      gap: 16px;
      padding: 8px 0;
      font-size: 16px;
    }

    :deep(.action-menu__item-wrapper + .action-menu__item-wrapper) {
      border-top: 1px solid $c-border-7;
    }
  }
}

.completion-menu-backdrop {
  display: none;

  @media (max-width: $w-mobile-s) {
    display: block;
    position: fixed;
    z-index: 9;
    inset: 0;
    background: rgba($c-black-1, 0.3);
  }
}

.night-mode .completion-menu__icon {
  &--highlights {
    background-image: url('@/assets.app/images/completion-menu/highlights-dark.svg');
  }

  &--exercises {
    background-image: url('@/assets.app/images/completion-menu/exercises-dark.svg');
  }

  &--save {
    background-image: url('@/assets.app/images/completion-menu/save-dark.svg');
  }

  &--saved {
    background-image: url('@/assets.app/images/completion-menu/save-filled.svg');
  }

  &--download {
    background-image: url('@/assets.app/images/completion-menu/download-dark.svg');
  }

  &--buy {
    background-image: url('@/assets.app/images/completion-menu/buy-dark.svg');
  }

  &--discuss {
    background-image: url('@/assets.app/images/completion-menu/discuss-dark.svg');
  }

  &--reset {
    background-image: url('@/assets.app/images/completion-menu/reset-dark.svg');
  }
}

.night-mode .completion-menu {
  &__toggle {
    border-color: $c-dark-8;
    color: $c-dark-8;
  }

  &__badge {
    background: $c-dark-5;
    color: $c-dark-4;

    &--highlight {
      background: $c-yellow;
      color: $c-dark-1;
    }

    &--complete {
      background: $c-green-2;
      color: $c-white;
    }
  }

  &__add-highlight {
    color: $c-dark-1;
  }

  :deep(.action-menu__item) {
    color: $c-dark-4;
  }

  @media (max-width: $w-mobile-s) {
    :deep(.action-menu__list) {
      border-color: $c-dark-1;
      background: $c-dark-2;

      &::before {
        background: $c-dark-5;
      }
    }

    :deep(.action-menu__item-wrapper + .action-menu__item-wrapper) {
      border-color: $c-dark-5;
    }
  }
}

.night-mode .completion-menu-backdrop {
  background: rgba($c-dark-1, 0.3);
}
</style>
